/**
 * The pure half of the RingCentral dialer bridge: everything the widget tells
 * us, reduced to the little the console needs from it.
 *
 * The console iframes the CRM's own `<crm>/dialer` page, which wraps
 * RingCentral Embeddable and relays postMessage both ways (see routes/dialer.js).
 * We listen to the `rc-*-notify` half and post `rc-adapter-*` commands back.
 * Kept free of React so it can be tested — the hook around it (useRcDialer)
 * owns the frame, the listener and the timers.
 *
 * Deliberately NOT mirrored here: mute, hold, keypad, transfer. The widget's
 * own in-call UI is what the panel shows, and a second set of controls would
 * only drift from it.
 */

/** One call as the SOFTPHONE sees it. */
export interface DialerCall {
  /** The widget's own session id — the handle every control command takes. */
  id: string;
  /** RingCentral's telephony session id, which is what a ring card is keyed on. */
  sessionKey: string | null;
  from: string | null;
  to: string | null;
  /** The other party's name, when the widget matched one. */
  name: string | null;
  direction: 'inbound' | 'outbound' | null;
}

/** The other party's number: who called, or who was called. */
export const peerOfCall = (call: DialerCall): string | null =>
  call.direction === 'outbound' ? call.to : (call.from ?? call.to);

export interface DialerState {
  /**
   * The widget has said whether it is signed in at least once. It announces
   * `loggedIn: false` while it boots, before restoring the session it holds on
   * its own origin, so anything that would prompt the agent to sign in waits
   * for this rather than flashing at everyone on every load.
   */
  known: boolean;
  /** The agent has signed the widget into their own RingCentral user. */
  loggedIn: boolean;
  /** The browser phone is registered — the precondition for answering here. */
  webphone: boolean;
  ringing: DialerCall[];
  /** A call is up in the widget, so the panel has something to control. */
  onCall: boolean;
  /** The call that is up, when the widget described it; `onCall` is the fact, this is its face. */
  active: DialerCall | null;
  /** Epoch ms the call came up — stamped here, on the edge, so a timer starts when the call did. */
  activeSince: number | null;
}

export const DIALER_IDLE: DialerState = {
  known: false,
  loggedIn: false,
  webphone: false,
  ringing: [],
  onCall: false,
  active: null,
  activeSince: null,
};

/**
 * The widget's session object, straight off the bridge — cross-origin and
 * untrusted, so every field is read defensively and a session with no id is
 * dropped rather than guessed at.
 */
export function parseWidgetCall(raw: unknown): DialerCall | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const call = raw as Record<string, unknown>;
  const id = call.id === undefined || call.id === null ? null : String(call.id);
  if (!id) return null;
  const party = call.partyData;
  // Webphone sessions carry the telephony session under partyData; newer
  // payloads also put it at the top level.
  const session =
    call.telephonySessionId ??
    (typeof party === 'object' && party !== null ? (party as Record<string, unknown>).sessionId : null);
  const match = call.contactMatch;
  const matched = typeof match === 'object' && match !== null ? (match as Record<string, unknown>).name : null;
  const name = typeof matched === 'string' ? matched : call.fromUserName;
  return {
    id,
    sessionKey: session === undefined || session === null ? null : String(session),
    from: typeof call.from === 'string' ? call.from : null,
    to: typeof call.to === 'string' ? call.to : null,
    name: typeof name === 'string' && name.trim() !== '' ? name : null,
    direction: call.direction === 'Outbound' ? 'outbound' : call.direction === 'Inbound' ? 'inbound' : null,
  };
}

const without = (calls: DialerCall[], id: string) => calls.filter((call) => call.id !== id);

/**
 * One `rc-*-notify` message applied to the state. Unknown shapes fall through
 * UNCHANGED — returning the same object is what keeps React from re-rendering
 * the whole module on the widget's chatter, which is constant.
 */
export function reduceWidget(state: DialerState, data: unknown, now: number = Date.now()): DialerState {
  if (typeof data !== 'object' || data === null) return state;
  const message = data as Record<string, unknown>;
  switch (typeof message.type === 'string' ? message.type : '') {
    case 'rc-login-status-notify': {
      const loggedIn = message.loggedIn === true;
      if (loggedIn) return state.loggedIn && state.known ? state : { ...state, known: true, loggedIn: true };
      // Signing out takes the phone with it; nothing it was holding survives —
      // except the knowledge that the widget has spoken, which is what lets a
      // sign-in prompt show at all.
      return state.loggedIn || state.webphone || state.ringing.length || state.onCall || !state.known
        ? { ...DIALER_IDLE, known: true }
        : state;
    }
    case 'rc-webphone-connection-status-notify': {
      // `ObjectMap.prefixKeys` values, e.g. `connectionStatus-connected`.
      const status = String(message.connectionStatus ?? '').split('-').pop();
      const webphone = status === 'connected';
      return webphone === state.webphone ? state : { ...state, webphone };
    }
    case 'rc-call-ring-notify': {
      const call = parseWidgetCall(message.call);
      if (!call) return state;
      return { ...state, ringing: [...without(state.ringing, call.id), call] };
    }
    case 'rc-call-init-notify':
    case 'rc-call-start-notify': {
      const call = parseWidgetCall(message.call);
      if (!call) return state;
      return {
        ...state,
        ringing: without(state.ringing, call.id),
        onCall: true,
        active: call,
        // Init and start both fire for one call: the stamp is the first's.
        activeSince: state.onCall && state.activeSince !== null ? state.activeSince : now,
      };
    }
    case 'rc-call-end-notify': {
      const call = parseWidgetCall(message.call);
      if (!call) return state;
      const ringing = without(state.ringing, call.id);
      return { ...state, ringing, onCall: false, active: null, activeSince: null };
    }
    default:
      return state;
  }
}

/**
 * The widget session behind a ring card, or null when this browser is not
 * ringing that call.
 *
 * Matching on the telephony session id is what ties a card to its own call.
 * The one ring the widget has stands in for it when the ids cannot be compared
 * — the session never named one, or [sole] says there is only one call in play
 * anyway. Anything else refuses: hanging up on the wrong caller is worse than
 * a card that says it cannot help.
 */
export function widgetRingFor(
  state: DialerState,
  sessionKey: string | null,
  sole: boolean,
): DialerCall | null {
  if (!state.ringing.length) return null;
  if (!sessionKey) return state.ringing[state.ringing.length - 1];
  const match = state.ringing.find((call) => call.sessionKey === sessionKey);
  if (match) return match;
  if (state.ringing.length !== 1) return null;
  const single = state.ringing[0];
  return single.sessionKey === null || sole ? single : null;
}

/**
 * Whether one `rc-*-notify` message says a call has started — the widget's own
 * word that a `rc-adapter-new-call` took. `init` and `start` are the browser
 * phone's; `ringout` is what the widget says instead when the agent has set it
 * to call with their desk phone, where no web-phone event will ever come.
 */
export function startsCall(data: unknown): boolean {
  if (typeof data !== 'object' || data === null) return false;
  const type = (data as Record<string, unknown>).type;
  return (
    type === 'rc-call-init-notify' || type === 'rc-call-start-notify' || type === 'rc-ringout-call-notify'
  );
}

/**
 * A number as the widget's dial pad wants it: digits, a leading `+`, and the
 * `*`/`#` a menu may need — nothing else. A contact's number arrives in E.164
 * and a log row's straight off the provider, but a WhatsApp address
 * (`whatsapp:+1…`) or a formatted string with spaces has reached this before,
 * and the widget quietly fills its pad with garbage rather than dialling it.
 * Null when nothing dialable is left.
 */
export function dialNumber(to: string): string | null {
  const cleaned = to
    .trim()
    .replace(/^whatsapp:/i, '')
    .replace(/[^\d+*#]/g, '')
    .replace(/(?!^)\+/g, '');
  return cleaned.replace(/[^\d]/g, '').length >= 3 ? cleaned : null;
}

/** The dialer's state in a line, for the panel header and the Calls tab. */
export function dialerStatusLine(state: DialerState): string {
  if (!state.loggedIn) return 'Sign in to answer calls here';
  if (state.onCall) return 'On a call';
  if (state.ringing.length) return 'Ringing';
  return state.webphone ? 'Ready' : 'Connecting…';
}
